import TableContainer from "Common/TableContainer";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import {
  Download,
  FileText,
  Send,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Offcanvas } from "react-bootstrap";
// import { useDispatch } from "react-redux"; // No longer needed
import { Axios } from "api/axios";
import { formatDate } from "helpers/functions/formatDate";
import { formatLabel } from "helpers/functions/formatLabel";
// import { StatusCell } from "Components/Other/StatusCell";
import { DetailedListingActionMenu } from "Components/MenuActionButton/DetailedListingActionMenu";
// import { useNavigate } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import { useHandleDownloadPDF } from "helpers/functions/handleDownloadPDF";
import { useHandleDownloadPDF2 } from "helpers/functions/handleDownloadPDF2";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import QuotationDetailsModal from "./QuotationDetailsModal";

interface DetailedListingSheetProps {
  open: boolean;
  onClose: () => void;
  type: "total" | "sent" | "won" | "lost";
  dashboardFilters: {
    timePeriod: string;
    dateRange: { from: string; to: string };
    createdBy: string;
  };
}

export function DetailedListingSheet({
  open,
  onClose,
  type,
  dashboardFilters,
}: DetailedListingSheetProps) {
  const dispatch = useDispatch<any>();
  // const navigate = useNavigate();
  const [searchQuery] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<any>();
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [selectedQuotationId, setSelectedQuotationId] = useState<string | null>(
    null
  );

  // Use PDF download hooks
  const handleDownloadPDF = useHandleDownloadPDF();
  const handleDownloadPDF2 = useHandleDownloadPDF2();

  // const user = JSON.parse(localStorage.getItem("authUser") || "{}");
  // const isAdmin = user?.user_type === "Admin";

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // Clear values when drawer closes
  useEffect(() => {
    if (!open) {
      setTotalRecords(0);
      setTotalRevenue(0);
      setPageSize(10);
      setData([]);
    }
  }, [open]);

  // Fetch quotations based on type and filters
  useEffect(() => {
    if (open) {
      const fetchQuotations = async () => {
        try {
          setLoading(true);
          setError(null);

          // Build filters for the new API
          const filters: any = {};

          // Add date range filters
          if (dashboardFilters.dateRange.from) {
            filters.created_at_from = dashboardFilters.dateRange.from;
          }

          if (dashboardFilters.dateRange.to) {
            filters.created_at_to = dashboardFilters.dateRange.to;
          }

          // Add user filter
          if (dashboardFilters.createdBy) {
            filters.user_id = dashboardFilters.createdBy;
          }

          // Add status filter based on type
          if (type === "sent") {
            filters.current_status = "Sent,Won,Lost";
          } else if (type === "won") {
            filters.current_status = "Won";
          } else if (type === "lost") {
            filters.current_status = "Lost";
          }
          // } else if (type === "total") {
          //   // For total, include all statuses
          //   filters.current_status = ["sent", "won", "lost", "draft"];
          // }

          // Add pagination and sorting
          filters.page = pageIndex + 1;
          filters.page_size = pageSize;
          if (sortField) {
            filters.sort = sortField;
            filters.order = sortDirection;
          }
          if (searchQuery) {
            filters.search = searchQuery;
          }

          console.log("Fetching quotations with filters:", filters);

          // Make direct API call to the new endpoint
          const response = await Axios.get(
            "/api/quotation/dashboard-quotation-detail-list/",
            {
              params: filters,
            }
          );

          console.log("Response data:", response.data);
          setData(response.data?.data || []);
          setTotalRecords(response.data?.count || 0);
          setTotalRevenue(response.data?.total_revenue || 0);
        } catch (err: any) {
          console.error("Failed to fetch quotations:", err);
          setError(
            err.response?.data?.message ||
              err.message ||
              "Failed to fetch quotations"
          );
        } finally {
          setLoading(false);
        }
      };

      fetchQuotations();
    }
  }, [
    open,
    type,
    dashboardFilters,
    pageIndex,
    pageSize,
    sorting,
    searchQuery,
    sortField,
    sortDirection,
  ]);

  const handleViewQuotation = useCallback((id: string) => {
    setSelectedQuotationId(id);
    setShowQuotationModal(true);
  }, []);

  const handleCloseQuotationModal = useCallback(() => {
    setShowQuotationModal(false);
    setSelectedQuotationId(null);
  }, []);

  const handleExport = useCallback(async () => {
    try {
      // Build filters for the export API (same format as other exports)
      const params = new URLSearchParams();

      // Add date range filters
      if (dashboardFilters.dateRange.from) {
        params.append("created_at_from", dashboardFilters.dateRange.from);
      }

      if (dashboardFilters.dateRange.to) {
        params.append("created_at_to", dashboardFilters.dateRange.to);
      }

      // Add user filter
      if (dashboardFilters.createdBy) {
        params.append("user_id", dashboardFilters.createdBy);
      }

      // Add status filter based on type
      if (type === "sent") {
        params.append("current_status", "Sent,Won,Lost,PI Generated");
      } else if (type === "won") {
        params.append("current_status", "Won,PI Generated");
      } else if (type === "lost") {
        params.append("current_status", "Lost");
      }

      params.append("page", String(pageIndex + 1));
      params.append("page_size", String(pageSize));

      // Add sorting
      if (sortField) {
        params.append("sort", sortField);
        params.append("order", sortDirection);
      }

      // Add search query
      if (searchQuery) {
        params.append("search", searchQuery);
      }

      // Add export parameter
      params.append("export", "excel");

      console.log("Exporting quotations with params:", params.toString());

      // Step 1: First call – get the file_url
      const response = await Axios.get(
        `/api/quotation/dashboard-quotation-detail-list/?${params.toString()}`
      );

      const fileUrl = response.data?.file_url;
      if (!fileUrl) {
        throw new Error("No file_url found in API response");
      }

      // Step 2: Second call – download the file as blob
      const downloadResponse = await Axios.get(fileUrl, {
        responseType: "blob",
      });

      // Step 3: Get filename from file_url
      const filename = fileUrl.split("/").pop() || "Quotation.xlsx";

      // Trigger file download
      const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Export completed successfully!");
    } catch (err: any) {
      console.error("Export failed:", err);
      toast.error("Failed to export quotations");
    }
  }, [
    dashboardFilters,
    type,
    sortField,
    sortDirection,
    searchQuery,
    pageIndex,
    pageSize,
  ]);

  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "Quotation Number",
        accessorKey: "quotation_auto_id",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/quotation/column-filter-dropdown/?column_filter=quotation_auto_id",
        cell: ({ row }: { row: any }) => (
          <div className="fw-semibold text-primary mx-2">
            {row.original.quotation_auto_id || "-"}
          </div>
        ),
      },
      {
        header: "Customer/Company Name",
        accessorKey: "customer_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/quotation/column-filter-dropdown/?column_filter=customer_name",
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.original.customer_details.customer_name || "-"}
          </div>
        ),
      },
      {
        header: "Mobile Number",
        accessorFn: (row: any) => row.customer_details?.mobile_no || "-",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/customers/column-filter-dropdown/?column_filter=mobile_no",
      },
      {
        header: "Quotation Date",
        accessorKey: "quotation_date",
        enableColumnFilter: false,
        filterType: "date" as const,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const date = formatDate(row.original.quotation_date);
          return `${date}`;
        },
      },
      {
        header: "Status",
        accessorKey: "current_status",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => (
          <span
            className={`badge text-white ${
              row.original.current_status === "Draft"
                ? "bg-primary"
                : row.original.current_status === "Sent"
                ? "bg-warning"
                : row.original.current_status === "Won"
                ? "bg-success"
                : row.original.current_status === "Lost"
                ? "bg-danger"
                : row.original.current_status === "PI Generated"
                ? "bg-success"
                : "bg-info"
            } text-dark`}
          >
            {row.original.current_status || "-"}
          </span>
        ),
      },
      {
        header: "Quotation Mode",
        accessorKey: "quotation_creation_type",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        cell: ({ row }: any) =>
          formatLabel(row.original.quotation_creation_type) || "-",
        filterFetchUrl:
          "/api/quotation/column-filter-dropdown/?column_filter=quotation_creation_type",
      },
      {
        header: "Amount",
        accessorKey: "quotation_total_amount",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: any) => {
          return (
            <span className="fw-semibold text-primary">
              ₹{" "}
              {Number(row.original.quotation_total_amount || 0).toLocaleString(
                "en-IN",
                { maximumFractionDigits: 2 }
              )}
            </span>
          );
        },
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        size: 80,
        cell: ({ row }: any) => (
          <div onClick={(e) => e.stopPropagation()}>
            <DetailedListingActionMenu
              row={row}
              // handleDelete={handleDelete}
              handleDownloadPDF={handleDownloadPDF}
              handleDownloadPDF2={handleDownloadPDF2}
              handleViewModal={handleViewQuotation}
              canChange={false}
              canDelete={false}
            />
          </div>
        ),
      },
    ],
    [
      // handleDelete,
      handleDownloadPDF,
      handleDownloadPDF2,
      handleViewQuotation,
      // isAdmin,
    ]
  );

  const getTitle = () => {
    switch (type) {
      case "total":
        return "Total Quotations";
      case "sent":
        return "Sent Quotations";
      case "won":
        return "Won Quotations";
      case "lost":
        return "Lost Quotations";
      default:
        return "Quotations";
    }
  };

  const getIcon = () => {
    switch (type) {
      case "total":
        return <FileText size={24} style={{ color: "white" }} />;
      case "sent":
        return <Send size={24} style={{ color: "white" }} />;
      case "won":
        return <TrendingUp size={24} style={{ color: "white" }} />;
      case "lost":
        return <TrendingDown size={24} style={{ color: "white" }} />;
      default:
        return <FileText size={24} style={{ color: "white" }} />;
    }
  };

  const getThemeColors = () => {
    switch (type) {
      case "total":
        return {
          primary: "#4481eb",
          secondary: "#04befe",
          gradient: "linear-gradient(120deg, #4481eb 0%, #04befe 100%)",
          lightGradient: "linear-gradient(135deg, #f0f7ff 0%, #e6f3ff 100%)",
        };
      case "sent":
        return {
          primary: "#9d50bb",
          secondary: "#6e48aa",
          gradient:
            "linear-gradient(120deg, rgb(199, 106, 0) 0%,rgb(255, 195, 85) 100%)",
          lightGradient: "linear-gradient(135deg, #f8f0ff 0%, #f0e6ff 100%)",
        };
      case "won":
        return {
          primary: "#43e97b",
          secondary: "#38f9d7",
          gradient:
            "linear-gradient(120deg, rgb(2, 172, 59) 0%,rgb(56, 249, 101) 100%)",
          lightGradient: "linear-gradient(135deg, #f0fff4 0%, #e6fff0 100%)",
        };
      case "lost":
        return {
          primary: "#fd5e53",
          secondary: "#ff7e5f",
          gradient: "linear-gradient(120deg, #fd5e53 0%, #ff7e5f 100%)",
          lightGradient: "linear-gradient(135deg, #fff0f0 0%, #ffe6e6 100%)",
        };
      default:
        return {
          primary: "#4481eb",
          secondary: "#04befe",
          gradient: "linear-gradient(120deg, #4481eb 0%, #04befe 100%)",
          lightGradient: "linear-gradient(135deg, #f0f7ff 0%, #e6f3ff 100%)",
        };
    }
  };

  const themeColors = getThemeColors();

  return (
    <Offcanvas
      show={open}
      onHide={onClose}
      placement="end"
      size="xl"
      style={{
        width: window.innerWidth >= 1000 ? "90%" : "100%",
      }}
      className="modern-drawer"
    >
      <style>
        {`
          .modern-drawer {
            box-shadow: -10px 0 30px rgba(0, 0, 0, 0.15);
            border-left: 1px solid rgba(0, 0, 0, 0.1);
          }
          
          .modern-drawer .offcanvas-header {
            background: ${themeColors.gradient};
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            padding: 1.5rem 2rem;
            position: relative;
            overflow: hidden;
          }
          
          .modern-drawer .offcanvas-header::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%, rgba(255, 255, 255, 0.05) 100%);
            pointer-events: none;
          }
          
          .modern-drawer .offcanvas-header .btn-close {
            filter: brightness(0) invert(1);
            opacity: 0.8;
            transition: all 0.3s ease;
            background-size: 1.2em;
            width: 1.2em;
            height: 1.2em;
          }
          
          .modern-drawer .offcanvas-header .btn-close:hover {
            opacity: 1;
            transform: scale(1.1);
          }
          
          .modern-drawer .offcanvas-title {
            color: white;
            position: relative;
            z-index: 1;
          }
          
          .modern-drawer .offcanvas-body {
            background: #f8f9fa;
            padding: 0;
            position: relative;
          }
          
          .drawer-content-wrapper {
            background: white;
            margin: 1.5rem;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            overflow: hidden;
            border: 1px solid rgba(0, 0, 0, 0.05);
          }
          
          .drawer-header-info {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            padding: 1.5rem;
            border-bottom: 1px solid rgba(0, 0, 0, 0.05);
            position: relative;
          }
          
          .drawer-header-info::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: ${themeColors.gradient};
          }
          
          .drawer-icon-wrapper {
            width: 60px;
            height: 60px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: ${themeColors.gradient};
            box-shadow: 0 8px 20px ${themeColors.primary}33;
            margin-bottom: 1rem;
          }
          
          .drawer-title {
            font-size: 1.5rem;
            font-weight: 700;
            color: #2c3e50;
            margin-bottom: 0.5rem;
            line-height: 1.2;
          }
          
          .drawer-subtitle {
            color: #6c757d;
            font-size: 0.95rem;
            font-weight: 500;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
          
          .drawer-subtitle::before {
            content: '📊';
            font-size: 1.1rem;
          }
          
          .drawer-stats {
            display: flex;
            gap: 1.5rem;
            margin-top: 1rem;
            flex-wrap: wrap;
          }
          
          .drawer-stat-item {
            background: white;
            padding: 0.75rem 1rem;
            border-radius: 8px;
            border: 1px solid rgba(0, 0, 0, 0.08);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
            min-width: 120px;
          }
          
          .drawer-stat-label {
            font-size: 0.8rem;
            color: #6c757d;
            font-weight: 500;
            margin-bottom: 0.25rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .drawer-stat-value {
            font-size: 1.25rem;
            font-weight: 700;
            color: #2c3e50;
          }
          
          .drawer-table-wrapper {
            padding: 0;
          }
        `}
      </style>

      <Offcanvas.Header>
        <Offcanvas.Title>
          <div className="d-flex align-items-center gap-3">
            <span
              style={{
                background: themeColors.gradient,
                borderRadius: "50%",
                padding: "0.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {getIcon()}
            </span>
            <div>
              <h5 className="mb-0">
                {getTitle()}
                {!loading && totalRecords > 0 && ` (${totalRecords})`}
              </h5>
              {!loading && totalRevenue > 0 && (
                <small className="text-white">
                  ₹{" "}
                  {Number(totalRevenue || 0).toLocaleString("en-IN", {
                    maximumFractionDigits: 2,
                  })}{" "}
                  Total {type === "won" ? "Revenue" : "Amount"}
                </small>
              )}
            </div>
          </div>
        </Offcanvas.Title>
        <div className="d-flex align-items-center gap-2">
          {data.length > 0 && (
            <button
              onClick={handleExport}
              className="btn btn-light btn-sm d-flex align-items-center gap-2"
              style={{
                background: "rgba(255, 255, 255, 0.9)",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                color: "#2c3e50",
                fontWeight: "500",
                borderRadius: "8px",
                padding: "0.5rem 1rem",
                transition: "all 0.3s ease",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 1)";
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 0, 0, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.9)";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 2px 8px rgba(0, 0, 0, 0.1)";
              }}
            >
              <Download size={16} />
              Export Excel
            </button>
          )}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          ></button>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <div className="drawer-content-wrapper">
          {/* <div className="drawer-header-info"> */}
          {/* <div className="d-flex align-items-start"> */}
          {/* <div className="drawer-icon-wrapper">{getIcon()}</div> */}
          {/* <div className="flex-grow-1"> */}
          {/* <h4 className="drawer-title">{getTitle()}</h4>
                <div className="drawer-subtitle">
                  Detailed view of {type} quotations
                </div> */}
          {/* <div className="drawer-stats">
                  <div className="drawer-stat-item">
                    <div className="drawer-stat-label">Total Records</div>
                    <div className="drawer-stat-value">{data.length}</div>
              </div>
                  <div className="drawer-stat-item">
                    <div className="drawer-stat-label">Current Page</div>
                    <div className="drawer-stat-value">{pageIndex + 1}</div>
            </div>
                  <div className="drawer-stat-item">
                    <div className="drawer-stat-label">Total Pages</div>
                    <div className="drawer-stat-value">
                      {Math.ceil(totalRecords / pageSize)}
              </div>
            </div>
                </div> */}
          {/* </div> */}
          {/* </div> */}
          {/* </div> */}

          <div className="p-4" style={{ paddingLeft: "1px" }}>
            <LoaderErrorWrapper loading={loading} error={error}>
              <TableContainer
                columns={columns}
                data={data}
                totalRecords={totalRecords}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                colValue={setColumnFilters}
                sorting={sorting}
                onSortingChange={setSorting}
                isBordered={false}
                isPagination={true}
                isGlobalFilter={false}
                SearchPlaceholder="Search ..."
                manualSorting={true}
              />
            </LoaderErrorWrapper>
          </div>
        </div>
      </Offcanvas.Body>

      {/* Quotation Details Modal */}
      <QuotationDetailsModal
        show={showQuotationModal}
        onHide={handleCloseQuotationModal}
        quotationId={selectedQuotationId}
      />
    </Offcanvas>
  );
}
